import { Link, useParams } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ArticleCard from "@/components/base/ArticleCard";
import { articles } from "@/mocks/articles";
import { useSeo, SITE_ORIGIN, SITE_NAME } from "@/hooks/useSeo";

export default function ArticleDetail() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((item) => item.id === id);

  const related = article
    ? articles
        .filter((item) => item.id !== article.id && item.category === article.category)
        .concat(articles.filter((item) => item.id !== article.id && item.category !== article.category))
        .slice(0, 3)
    : [];

  useSeo({
    title: article
      ? `${article.title} - 瓜圈显微镜`
      : "内容不存在 - 瓜圈显微镜",
    description: article
      ? `${article.excerpt}（选美吃瓜投票打榜观察，作者：${article.author}）`
      : "该内容可能已被调整或移除，你可以返回打榜观察栏目浏览其他内容。",
    keywords: article ? `选美吃瓜,${article.tags.join(",")},选美吃瓜投票打榜` : "选美吃瓜,打榜观察",
    path: `/article/${id ?? ""}`,
    image: article?.cover,
    type: "article",
    jsonLd: article
      ? [
          {
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            headline: article.title,
            description: article.excerpt,
            image: article.cover,
            datePublished: article.date,
            dateModified: article.date,
            articleSection: article.category,
            keywords: article.tags.join(","),
            inLanguage: "zh-CN",
            author: { "@type": "Person", name: article.author },
            publisher: { "@type": "Organization", name: SITE_NAME },
            mainEntityOfPage: `${SITE_ORIGIN}/article/${article.id}`,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "打榜观察", item: `${SITE_ORIGIN}/ranking` },
              {
                "@type": "ListItem",
                position: 3,
                name: article.title,
                item: `${SITE_ORIGIN}/article/${article.id}`,
              },
            ],
          },
        ]
      : undefined,
  });

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col bg-background-50">
        <SiteHeader />
        <main className="flex-1 flex items-center justify-center px-4 py-24">
          <div className="text-center max-w-[520px]">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-primary-100 text-primary-700 mb-5">
              <i className="ri-article-line text-3xl" />
            </span>
            <h1 className="font-heading text-2xl font-bold text-foreground-950">没有找到这篇内容</h1>
            <p className="mt-3 text-sm text-foreground-600">
              它可能被调整过位置，或者链接有误。你可以回到打榜观察栏目继续浏览。
            </p>
            <Link
              to="/ranking"
              className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-arrow-left-line" />
              返回打榜观察
            </Link>
          </div>
        </main>
        <SiteFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-background-50">
      <SiteHeader />
      <main className="flex-1">
        <article>
          <header className="bg-background-100 border-b border-background-200">
            <div className="px-4 md:px-6 xl:px-10 py-8 md:py-12">
              <div className="max-w-[900px] mx-auto">
                <Breadcrumbs
                  items={[
                    { label: "首页", to: "/" },
                    { label: "打榜观察", to: "/ranking" },
                    { label: article.category, to: `/ranking?category=${encodeURIComponent(article.category)}` },
                  ]}
                />
                <span className="inline-block mt-6 px-2.5 py-1 text-[11px] rounded bg-primary-100 text-primary-700">
                  {article.category}
                </span>
                <h1 className="mt-4 font-heading text-2xl md:text-4xl font-black leading-snug text-foreground-950">
                  {article.title}
                </h1>
                <p className="mt-5 text-base leading-relaxed text-foreground-600">{article.excerpt}</p>
                <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs text-foreground-500">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-user-line" />
                    {article.author}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-calendar-line" />
                    {article.date}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-time-line" />
                    约 {article.readTime}
                  </span>
                </div>
              </div>
            </div>
          </header>

          <div className="px-4 md:px-6 xl:px-10 py-10 md:py-14">
            <div className="max-w-[1320px] mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_320px] gap-10 lg:gap-14">
              <div className="min-w-0">
                <figure className="rounded-lg overflow-hidden bg-background-100 mb-10">
                  <div className="w-full aspect-[16/9]">
                    <img
                      src={article.cover}
                      alt={article.coverAlt}
                      title={`${article.title} - 选美吃瓜`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <figcaption className="px-4 py-3 text-xs text-foreground-500 bg-background-100">
                    {article.coverAlt}（内容仅作现象观察与讨论）
                  </figcaption>
                </figure>

                <div className="prose-custom max-w-[720px]">
                  {article.sections.map((section) => (
                    <section key={section.heading} className="mb-9">
                      <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950 mb-4 flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-5 rounded-full bg-primary-500 shrink-0" />
                        {section.heading}
                      </h2>
                      <div className="flex flex-col gap-4">
                        {section.paragraphs.map((paragraph, index) => (
                          <p
                            key={`${section.heading}-${index}`}
                            className="text-[15px] md:text-base leading-[1.9] text-foreground-700"
                          >
                            {paragraph}
                          </p>
                        ))}
                      </div>
                    </section>
                  ))}
                </div>

                <div className="mt-10 pt-6 border-t border-background-200 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-foreground-500 mr-1">标签：</span>
                  {article.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/ranking?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 text-xs rounded-full bg-secondary-100 text-secondary-900 hover:bg-secondary-200 transition-colors cursor-pointer"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>
              </div>

              <aside className="lg:sticky lg:top-24 h-fit flex flex-col gap-6">
                <div className="bg-background-100 border border-background-200 rounded-lg p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <i className="ri-list-check-2 text-primary-600" />
                    本文小节
                  </h2>
                  <ol className="flex flex-col gap-3">
                    {article.sections.map((section, index) => (
                      <li key={section.heading} className="flex gap-3 text-sm text-foreground-600">
                        <span className="font-heading text-primary-600 font-bold">
                          {String(index + 1).padStart(2, "0")}
                        </span>
                        <span className="leading-snug">{section.heading}</span>
                      </li>
                    ))}
                  </ol>
                </div>

                <div className="bg-accent-100 border border-accent-200 rounded-lg p-5">
                  <h2 className="font-heading text-base font-bold text-foreground-950 mb-2">
                    吃瓜也讲方法
                  </h2>
                  <p className="text-sm leading-relaxed text-foreground-700">
                    看到票数异常，先别急着下结论。把截图时间、账号结构和官方说明放在一起看，判断会更稳。
                  </p>
                  <Link
                    to="/about#principles"
                    className="mt-4 inline-flex items-center gap-1 text-sm text-accent-800 hover:text-accent-900 cursor-pointer whitespace-nowrap"
                  >
                    了解我们的内容原则
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </aside>
            </div>
          </div>

          <section className="bg-background-100 border-t border-background-200">
            <div className="px-4 md:px-6 xl:px-10 py-12 md:py-16">
              <div className="max-w-[1320px] mx-auto">
                <div className="flex items-end justify-between gap-4 mb-8">
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-950">相关内容</h2>
                  <Link
                    to="/ranking"
                    className="text-sm text-primary-700 hover:text-primary-800 whitespace-nowrap cursor-pointer"
                  >
                    全部内容
                  </Link>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                  {related.map((item) => (
                    <ArticleCard key={item.id} article={item} />
                  ))}
                </div>
              </div>
            </div>
          </section>
        </article>
      </main>
      <SiteFooter />
      <BackToTop />
    </div>
  );
}